import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  FormModal,
  Input,
  Page,
  Select,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { useConfirm, useTableSort } from "@marekl/adminer/hooks";
import { pluralizeCzech } from "@marekl/adminer/format";
import { TeamFlag } from "../../../components/TeamFlag";
import { AppTopbar } from "../../../shell/AppTopbar";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import {
  useCreateFederation,
  useDeleteFederation,
  useFederations,
  useUpdateFederation,
  type Federation,
} from "../../../lib/queries/federations";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; federation: Federation };

// ČNSMS and WMS are seeded and their roles are facts of the deployment, so
// the page states which is which rather than offering it as a choice.
function roleLabel(f: Federation): string {
  if (f.is_primary) return "Domácí federace";
  if (f.is_umbrella) return "Zastřešující federace";
  return "Národní federace";
}

function roleBadge(f: Federation) {
  return <Badge variant={f.is_primary ? "info" : "muted"}>{roleLabel(f)}</Badge>;
}

function countryName(code: string): string {
  if (!code) return "—";
  return COUNTRY_OPTIONS.find((c) => c.value === code)?.label ?? code;
}

export function FederationsPage() {
  const { data: me } = useMe();
  // Čtení všeho lists the federations and changes none of them.
  const readOnly = readsOnlyAsGlobalViewer(me);
  const { data: federations = [], isLoading, error, refetch } = useFederations();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [form, setForm] = useState({ name: "", short_name: "", country_code: "" });

  const create = useCreateFederation();
  const { mutateAsync: deleteFederation } = useDeleteFederation();
  const confirm = useConfirm();
  const editing = modal.kind === "edit" ? modal.federation : null;
  const update = useUpdateFederation(editing?.id ?? "");

  const close = useCallback(() => {
    setModal({ kind: "closed" });
    setForm({ name: "", short_name: "", country_code: "" });
    create.reset();
    update.reset();
  }, [create, update]);

  const openCreate = () => {
    setForm({ name: "", short_name: "", country_code: "" });
    setModal({ kind: "create" });
  };

  const openEdit = useCallback((f: Federation) => {
    setForm({ name: f.name, short_name: f.short_name, country_code: f.country_code });
    setModal({ kind: "edit", federation: f });
  }, []);

  const submit = () => {
    if (modal.kind === "create") {
      create.mutate(form, { onSuccess: close });
    } else if (modal.kind === "edit") {
      update.mutate(form, { onSuccess: close });
    }
  };

  // The umbrella belongs to no state, so its country is neither asked for
  // nor required.
  const countryRequired = !editing?.is_umbrella;
  const canSubmit =
    form.name.trim().length > 0 && (!countryRequired || form.country_code.length === 2);

  // useCallback because the columns are memoised for useTableSort.
  const askDelete = useCallback(
    (f: Federation) =>
      confirm({
        title: "Smazat federaci",
        variant: "danger",
        description: (
          <>
            Smazat federaci <strong>{f.name}</strong>?
            {f.admins_count > 0 && (
              <>
                {" "}
                Zanikne tím i role správce federace, kterou pro ni má{" "}
                <strong>{f.admins_count}</strong>{" "}
                {pluralizeCzech(f.admins_count, ["uživatel", "uživatelé", "uživatelů"])}.
              </>
            )}
          </>
        ),
        confirmLabel: "Smazat",
        onConfirm: async () => {
          await deleteFederation(f.id);
        },
      }),
    [confirm, deleteFederation],
  );

  const columns = useMemo<Column<Federation>[]>(
    () => [
      {
        key: "name",
        header: "Název",
        primary: true,
        sortable: true,
        sortValue: (f) => f.name,
        render: (f) => f.name,
      },
      {
        key: "short_name",
        header: "Zkratka",
        sortable: true,
        sortValue: (f) => f.short_name || null,
        render: (f) =>
          f.short_name ? f.short_name : <Text variant="muted">—</Text>,
      },
      {
        key: "country",
        header: "Stát",
        sortable: true,
        sortValue: (f) => (f.country_code ? countryName(f.country_code) : null),
        // Vlajka tu rozlišuje, na rozdíl od startovní listiny: každá
        // federace je z jiného státu, takže sloupec stejných vlajek
        // nevznikne. Zastřešující federace nemá stát žádný a prázdno se
        // čte správně jako „celosvětová".
        render: (f) =>
          f.country_code ? (
            <>
              <TeamFlag code={f.country_code} withCode={false} />{" "}
              {countryName(f.country_code)} <Text variant="muted">{f.country_code}</Text>
            </>
          ) : (
            <Text variant="muted">bez státu</Text>
          ),
      },
      {
        key: "teams",
        header: "Týmy",
        sortable: true,
        sortValue: (f) => f.teams_count,
        render: (f) => f.teams_count,
      },
      {
        key: "role",
        header: "Role",
        sortable: true,
        sortValue: (f) => roleLabel(f),
        render: (f) => roleBadge(f),
      },
      {
        key: "actions",
        header: "",
        render: (f) => {
          if (readOnly) return null;
          // ČNSMS and the umbrella are seeded and resolved by flag, so the
          // server refuses to delete them; offering the button would only
          // promise something it will not do.
          const deletable = !f.is_primary && !f.is_umbrella;
          return (
            <Table.Actions>
              <Table.Action appearance="button" onClick={() => openEdit(f)}>
                Upravit
              </Table.Action>
              {deletable && (
                <Table.Action
                  appearance="button"
                  variant="danger"
                  disabled={f.teams_count > 0}
                  title={
                    f.teams_count > 0
                      ? "Nejdřív přesuňte týmy pod jinou federaci"
                      : undefined
                  }
                  onClick={() => askDelete(f)}
                >
                  Smazat
                </Table.Action>
              )}
            </Table.Actions>
          );
        },
      },
    ],
    [openEdit, askDelete, readOnly],
  );

  const tableSort = useTableSort(federations, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "name", dir: "asc" },
  });

  return (
    <>
      <AppTopbar crumbs={[{ label: "Federace" }]} />
      <Page>
        <Page.Header
          title="Federace"
          subtitle="federace, pod které patří týmy"
          actions={
            !readOnly && (
              <Button variant="primary" onClick={openCreate}>
                Nová federace
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          <Table<Federation>
            framed
            {...tableSort.tableProps}
            rowKey={(f) => f.id}
            columns={columns}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            cards="sm"
            empty={
              <EmptyState
                title="Žádné federace"
                description="Zatím není evidovaná žádná federace."
              />
            }
          />
        </Page.Content>
      </Page>

      <FormModal
        open={modal.kind !== "closed"}
        title={modal.kind === "edit" ? "Upravit federaci" : "Nová federace"}
        submitLabel={modal.kind === "edit" ? "Uložit" : "Vytvořit"}
        busy={create.isPending || update.isPending}
        canSubmit={canSubmit}
        error={create.error ?? update.error}
        onSubmit={submit}
        onClose={close}
      >
        <Input
          label="Název"
          required
          value={form.name}
          placeholder="Slovenská federácia mažoretkového športu"
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Zkratka"
          value={form.short_name}
          placeholder="SFMŠ"
          maxLength={32}
          hint="Používá se tam, kde by celý název překážel, třeba v seznamu týmů. Bez zkratky se všude píše celý název."
          onChange={(e) => setForm({ ...form, short_name: e.target.value })}
        />
        {countryRequired && (
          <Select
            label="Stát"
            required
            value={form.country_code}
            onChange={(e) => setForm({ ...form, country_code: e.target.value })}
            hint="Nová federace je vždy národní. Domácí a zastřešující federaci zakládá systém."
          >
            <option value="">Vyberte stát</option>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
        )}
      </FormModal>
    </>
  );
}
